import React, { createContext, useContext, useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";

const CompanyContext = createContext({
  company: null,
  setCompany: () => {},
  loading: true,
  hasNoCompany: false,
  loadFailed: false,
  retryLoad: () => {},
});

export function CompanyProvider({ children }) {
  const { email, token: authToken } = useAuth();
  const [company, setCompanyState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasNoCompany, setHasNoCompany] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const setCompany = (c) => {
    setCompanyState(c);
    if (c && c._id) {
      localStorage.setItem("demoCompanyId", c._id);
    }
  };

  const retryLoad = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    const token = authToken || localStorage.getItem("authEmail");
    if (!token) {
      setLoading(false);
      setCompanyState(null);
      setHasNoCompany(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadFailed(false);
    const savedCompanyId = localStorage.getItem("demoCompanyId");

    // Ein Versuch: gespeicherte Firma laden, sonst Firmenliste.
    // Netzwerk-/Serverfehler werfen → äussere Retry-Schleife übernimmt.
    const attemptLoad = async () => {
      if (savedCompanyId) {
        try {
          const res = await apiClient.get(`/my/companies/${savedCompanyId}`);
          if (!res.data?.archived) return { company: res.data };
          // Iter239: gespeicherte Firma ist archiviert → auf aktive Firma wechseln
        } catch (e) {
          const st = e?.response?.status;
          if (!st || st >= 500 || st === 429) throw e; // Cold-Start/Netzwerk → retry
          // 401/403/404 → auf Firmenliste ausweichen
        }
      }
      const listRes = await apiClient.get(`/my/companies`);
      const companies = listRes.data || [];
      if (companies.length > 0) return { company: companies[0], persist: true };
      return { empty: true };
    };

    const loadCompany = async () => {
      // Iter288: Beim Kaltstart schlagen erste Requests gern fehl → bis zu 4 Versuche
      for (let attempt = 0; attempt < 4; attempt++) {
        try {
          const r = await attemptLoad();
          if (cancelled) return;
          if (r.empty) {
            setHasNoCompany(true);
            setCompanyState(null);
          } else {
            setCompanyState(r.company);
            setHasNoCompany(false);
            if (r.persist) localStorage.setItem("demoCompanyId", r.company._id);
          }
          setLoading(false);
          return;
        } catch (e) {
          if (cancelled) return;
          console.error(`Firma laden fehlgeschlagen (Versuch ${attempt + 1}/4)`, e);
          await new Promise((res) => setTimeout(res, 1200 * (attempt + 1)));
          if (cancelled) return;
        }
      }
      setLoadFailed(true);
      setLoading(false);
    };
    loadCompany();
    return () => { cancelled = true; };
    // Re-run whenever auth email/token changes (login/logout) or manual retry
  }, [email, authToken, reloadKey]);

  return (
    <CompanyContext.Provider value={{ company, setCompany, loading, hasNoCompany, loadFailed, retryLoad }}>
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  return useContext(CompanyContext);
}
